import { TrendingUp, TrendingDown, Calendar, DollarSign, Percent, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface FinanceKPIsProps {
  taxaInadimplencia: number;
  prazoMedioRecebimento: number;
  prazoMedioPagamento: number;
  ticketMedioRecebimento: number;
  ticketMedioPagamento: number;
  taxaPontualidadeRecebimento: number;
  taxaPontualidadePagamento: number;
}

export const FinanceKPIs = ({
  taxaInadimplencia,
  prazoMedioRecebimento,
  prazoMedioPagamento,
  ticketMedioRecebimento,
  ticketMedioPagamento,
  taxaPontualidadeRecebimento,
  taxaPontualidadePagamento,
}: FinanceKPIsProps) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Percent className="h-4 w-4" />
            Taxa de Inadimplência
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${taxaInadimplencia > 10 ? 'text-red-500' : taxaInadimplencia > 5 ? 'text-yellow-500' : 'text-green-500'}`}>
            {taxaInadimplencia.toFixed(1)}%
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {taxaInadimplencia > 10 ? 'Atenção necessária' : taxaInadimplencia > 5 ? 'Monitorar' : 'Dentro do esperado'}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Prazo Médio Recebimento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${prazoMedioRecebimento > 0 ? 'text-red-500' : 'text-green-500'}`}>
            {prazoMedioRecebimento > 0 ? '+' : ''}{prazoMedioRecebimento} dias
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {prazoMedioRecebimento > 0 ? 'Após o vencimento' : 'Antes do vencimento'}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Prazo Médio Pagamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${prazoMedioPagamento > 0 ? 'text-red-500' : 'text-green-500'}`}>
            {prazoMedioPagamento > 0 ? '+' : ''}{prazoMedioPagamento} dias
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {prazoMedioPagamento > 0 ? 'Após o vencimento' : 'Antes do vencimento'}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Ticket Médio Recebimento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-foreground">
            {ticketMedioRecebimento.toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Valor médio</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Pontualidade Recebimento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${taxaPontualidadeRecebimento < 70 ? 'text-red-500' : taxaPontualidadeRecebimento < 85 ? 'text-yellow-500' : 'text-green-500'}`}>
            {taxaPontualidadeRecebimento.toFixed(1)}%
          </div>
          <p className="text-xs text-muted-foreground mt-1">Recebimentos no prazo</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <TrendingDown className="h-4 w-4" />
            Pontualidade Pagamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${taxaPontualidadePagamento < 70 ? 'text-red-500' : taxaPontualidadePagamento < 85 ? 'text-yellow-500' : 'text-green-500'}`}>
            {taxaPontualidadePagamento.toFixed(1)}%
          </div>
          <p className="text-xs text-muted-foreground mt-1">Pagamentos no prazo</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Ticket Médio Pagamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-foreground">
            {ticketMedioPagamento.toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Valor médio</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Ciclo Financeiro
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-foreground">
            {prazoMedioRecebimento - prazoMedioPagamento} dias
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Recebimento - Pagamento
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
